import { Link } from 'react-router-dom';
import { Star, ArrowRight, ArrowUpRight } from 'lucide-react';
import Seo, { breadcrumbLd, LOCAL_BUSINESS_LD } from '../components/Seo';
import { MaskedLines, FadeUp, Eyebrow } from '../components/motion';
import { useT } from '../i18n/LangContext';
import { useContent } from '../data/useContent';
import { GOOGLE_PROFILE_URL } from '../data/content';

export default function ReviewsPage() {
  const t = useT();
  const { REVIEWS } = useContent();

  return (
    <>
      <Seo title={t['seo.reviews.title']} description={t['seo.reviews.desc']}
        jsonLd={[LOCAL_BUSINESS_LD, breadcrumbLd(['Home', 'Reviews'])]} />
      <section className="bg-ink pb-24 pt-16 md:pt-24">
        <div className="container-site">
          <Eyebrow>{t['rp.eyebrow']}</Eyebrow>
          <MaskedLines as="h1" className="mt-4 font-serif text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
            lines={[{ text: t['rp.h1a'], cls: 'text-white' }, { text: t['rp.h1b'], cls: 'italic text-gold' }]} />
          <FadeUp delay={0.3}>
            <a data-testid="reviews-page-summary" href={GOOGLE_PROFILE_URL} target="_blank" rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 px-6 py-4 transition-colors hover:border-gold/40">
              <span className="font-serif text-5xl font-semibold text-white">4.9</span>
              <div>
                <span className="flex text-gold">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-gold" />)}</span>
                <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-silver">20+ {t['rv.googleReviews']} <ArrowUpRight className="h-3 w-3" /></p>
              </div>
            </a>
          </FadeUp>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <FadeUp key={r.name} delay={(i % 3) * 0.08} className="h-full">
                <figure data-testid={`reviews-page-card-${i}`} className="flex h-full flex-col rounded-xl border border-white/10 bg-coal p-7">
                  <span className="flex text-gold">{[...Array(r.rating)].map((_, j) => <Star key={j} className="h-4 w-4 fill-gold" />)}</span>
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-white/75">"{r.text}"</blockquote>
                  <figcaption className="mt-5 border-t border-white/10 pt-4">
                    <p className="text-sm font-bold text-white">{r.name}</p>
                    <p className="text-xs text-silver">{r.area} · {r.service}</p>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>

          <FadeUp className="mt-14 flex flex-wrap items-center justify-center gap-5">
            <Link data-testid="reviews-page-cta" to="/quote" className="gold-btn text-base !px-10 !py-4">{t['rp.cta']} <ArrowRight className="h-5 w-5" /></Link>
            <a data-testid="reviews-page-google-link" href={GOOGLE_PROFILE_URL} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm font-bold text-gold transition-colors hover:text-gold-light">
              {t['rv.readOnGoogle']} <ArrowUpRight className="h-4 w-4" />
            </a>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
